// Video YouTube de fondo — multi-video con rotación. Store mínimo en localStorage.
// useSyncExternalStore desde YouTubeBackground, VideoSetup y AudioToggle.

const IDS_KEY = 'wiphub_video_ids'
const LEGACY_ID_KEY = 'wiphub_video_id'
const MUTE_KEY = 'wiphub_video_muted'
const ROTATION_MS_KEY = 'wiphub_video_rotation_ms'

export const DEFAULT_ROTATION_MS = 15 * 60_000

// Videos por default — la TV no tiene teclado cómodo, así que el dashboard
// arranca con esta lista hardcoded. El modal permite override desde el browser.
// Agregar IDs separados por coma para sumar más a la rotación por default.
export const DEFAULT_VIDEO_IDS: readonly string[] = [
  'tmlE1ct0cYk',
]

type Listener = () => void
const listeners = new Set<Listener>()

function notify() {
  listeners.forEach((l) => l())
}

export function subscribeVideo(cb: Listener): () => void {
  listeners.add(cb)
  return () => listeners.delete(cb)
}

// useSyncExternalStore requiere que getSnapshot retorne valor referencialmente
// estable cuando los datos no cambian. Sin este cache, getVideoIds() crea un array
// nuevo en cada render → loop infinito de re-renders → app crashea.
//
// Semántica de los 3 estados de IDS_KEY en localStorage:
//   - ausente (null)        → usa DEFAULT_VIDEO_IDS (para fresh install sin teclado)
//   - JSON `[]`             → "configurado vacío" explícito, sin video
//   - JSON array non-empty  → custom override
let cachedIds: string[] | null = null
const DEFAULTS_ARRAY: string[] = [...DEFAULT_VIDEO_IDS]

function readVideoIdsFromStorage(): string[] {
  const raw = localStorage.getItem(IDS_KEY)
  if (raw !== null) {
    try {
      const parsed = JSON.parse(raw)
      if (Array.isArray(parsed)) {
        return parsed.filter((s): s is string => typeof s === 'string')
      }
    } catch {
      // continúa al legacy
    }
  }
  const legacy = localStorage.getItem(LEGACY_ID_KEY)
  if (legacy) return [legacy]
  return DEFAULTS_ARRAY
}

export function getVideoIds(): string[] {
  if (cachedIds === null) cachedIds = readVideoIdsFromStorage()
  return cachedIds
}

export function setVideoIds(ids: string[]) {
  // Persistir SIEMPRE el array (incluso vacío) para distinguir "configurado vacío"
  // de "nunca configurado". La key ausente vuelve a los defaults; `[]` significa
  // sin video por elección del usuario.
  localStorage.setItem(IDS_KEY, JSON.stringify(ids))
  localStorage.removeItem(LEGACY_ID_KEY)
  cachedIds = null
  notify()
}

// Para uso del modal: distinguir si la lista actual es default o custom.
export function isUsingDefault(): boolean {
  return localStorage.getItem(IDS_KEY) === null && localStorage.getItem(LEGACY_ID_KEY) === null
}

export function getMuted(): boolean {
  // default = muted (autoplay requirement de browsers)
  const v = localStorage.getItem(MUTE_KEY)
  return v === null ? true : v === 'true'
}

export function setMuted(m: boolean) {
  localStorage.setItem(MUTE_KEY, String(m))
  notify()
}

export function getRotationMs(): number {
  const raw = localStorage.getItem(ROTATION_MS_KEY)
  if (!raw) return DEFAULT_ROTATION_MS
  const parsed = Number(raw)
  return Number.isFinite(parsed) && parsed > 0 ? parsed : DEFAULT_ROTATION_MS
}

// Acepta:
//   - https://www.youtube.com/watch?v=abc123
//   - https://youtu.be/abc123
//   - https://www.youtube.com/playlist?list=PLxxx (playlist)
//   - ID directo (single video o playlist con prefijo PL/UU/LL)
//
// Retorna:
//   - { kind: 'video', id }
//   - { kind: 'playlist', id }
//   - null si no se puede parsear
export type VideoSource = { kind: 'video' | 'playlist'; id: string }

export function parseVideoInput(raw: string): VideoSource | null {
  const trimmed = raw.trim()
  if (!trimmed) return null

  // ID directo (videoId: 11 chars; playlistId: empieza PL/UU/LL/FL)
  if (/^[A-Za-z0-9_-]{11}$/.test(trimmed)) return { kind: 'video', id: trimmed }
  if (/^(PL|UU|LL|FL|RD|OL)[A-Za-z0-9_-]{10,}$/.test(trimmed)) {
    return { kind: 'playlist', id: trimmed }
  }

  try {
    const u = new URL(trimmed)
    const list = u.searchParams.get('list')
    if (list) return { kind: 'playlist', id: list }

    const v = u.searchParams.get('v')
    if (v) return { kind: 'video', id: v }

    if (u.hostname === 'youtu.be') {
      const id = u.pathname.replace(/^\//, '')
      if (id) return { kind: 'video', id }
    }
  } catch {
    return null
  }
  return null
}

// Parsea un bloque multi-línea de URLs/IDs. Ignora líneas vacías.
// Retorna { ids, errors: <líneas inválidas> }.
export function parseVideoList(raw: string): { ids: string[]; errors: string[] } {
  const lines = raw.split(/\r?\n/).map((l) => l.trim()).filter(Boolean)
  const ids: string[] = []
  const errors: string[] = []
  for (const line of lines) {
    const parsed = parseVideoInput(line)
    if (parsed) ids.push(parsed.id)
    else errors.push(line)
  }
  return { ids, errors }
}

export function buildEmbedSrc(stored: string, muted: boolean): string {
  // Detectar si el storedId es playlist (prefijos típicos) o video.
  const isPlaylist = /^(PL|UU|LL|FL|RD|OL)[A-Za-z0-9_-]{10,}$/.test(stored)
  const muteParam = muted ? 1 : 0
  const base = 'https://www.youtube.com/embed'
  const common = `autoplay=1&mute=${muteParam}&loop=1&controls=0&modestbranding=1&rel=0&iv_load_policy=3&playsinline=1`

  if (isPlaylist) {
    return `${base}/videoseries?list=${stored}&${common}`
  }
  // Single video: loop=1 requiere también playlist=<id> en YouTube embed.
  return `${base}/${stored}?playlist=${stored}&${common}`
}
